import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} SwitchAvisosRefs
 * @property {HTMLElement} row
 *
 * @typedef {Object} AvisoEntry
 * @property {string} id
 * @property {string} titulo
 * @property {string} texto
 * @property {string} cor_txt
 * @property {string} [cor_bg]
 * @property {string} [icone_url]
 * @property {boolean} bordas
 */

/**
 * Disclosure-pill row below the ATC button. Pre-merged per-variant lists
 * are embedded server-side as a JSON blob (see blocks/avisos.liquid); the
 * client just looks up the new variant's pre-merged list on variant:update
 * and rebuilds the row via SwitchMotion.collapseSwap() (GSAP timeline in
 * switch-animations.js — Decision #30).
 *
 * The motion override is intentional and DECISIONS.md-tracked — this is
 * the second sanctioned exception to the no-variant-swap-motion rule
 * (the first being switch-discount-badge's grid-rows collapse).
 *
 * @extends {Component<SwitchAvisosRefs>}
 */
class SwitchAvisos extends Component {
  /** @type {string[]} */
  #currentIds = [];
  /** @type {object|null} */
  #payload = null;

  connectedCallback() {
    super.connectedCallback();
    this.#cacheCurrentIds();
    this.#payload = this.#readPayload();
    const section = this.closest('.shopify-section');
    if (!section) return;
    // 4.x: ThemeEvents.variantUpdate -> StandardEvents.productSelect (@shopify/events).
    // Inert on single-variant Switch (Decision #24); handler guard no-ops until a
    // variant id is present. Adapt handleVariantUpdate to ProductSelectEvent's
    // event.promise/detail shape if multi-variant is ever introduced.
    section.addEventListener(StandardEvents.productSelect, this.handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section');
    if (section) {
      section.removeEventListener(StandardEvents.productSelect, this.handleVariantUpdate);
    }
  }

  #cacheCurrentIds() {
    const row = this.refs.row;
    if (!row) return;
    this.#currentIds = Array.from(row.querySelectorAll('[data-aviso-id]')).map(
      (el) => el.dataset.avisoId
    );
  }

  #readPayload() {
    const script = this.querySelector('script[data-switch-avisos-payload]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  handleVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    const productId = event.detail?.data?.productId;
    if (!variantId || !productId) return;

    // Combined listings can mount more than one block on a page; only react
    // to updates for our own product.
    if (String(productId) !== String(this.dataset.productId)) return;

    const payload = this.#payload;
    if (!payload || !payload.variants) return;
    const newList = payload.variants[variantId];
    if (!Array.isArray(newList)) return;

    const newIds = newList.map((a) => a.id);
    if (this.#arraysEqual(newIds, this.#currentIds)) return;

    this.#runTransition(newList, newIds);
  };

  #arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  #runTransition(newList, newIds) {
    const row = this.refs.row;
    if (!row) {
      this.#currentIds = newIds;
      return;
    }

    this.#currentIds = newIds;
    const swap = () => this.#rebuildRow(newList);

    // Motion is delegated to the GSAP layer (Decision #30) — SwitchMotion
    // is a classic-defer global, so it's lazily resolved at interaction
    // time; absent (load failure) → instant swap, content never blocked.
    if (window.SwitchMotion) {
      window.SwitchMotion.collapseSwap(row, swap);
    } else {
      swap();
    }
  }

  #rebuildRow(newList) {
    const row = this.refs.row;
    if (!row) return;
    row.replaceChildren();
    for (let i = 0; i < newList.length; i++) {
      row.append(this.#createPill(newList[i], i));
    }
  }

  /**
   * @param {AvisoEntry} aviso
   * @param {number} index
   */
  #createPill(aviso, index) {
    const blockId = this.dataset.blockId;
    const popoverId = `switch-aviso-popover-${blockId}-${index}`;
    const srId = `switch-aviso-sr-${blockId}-${index}`;
    const anchorName = `--switch-aviso-anchor-${blockId}-${index}`;

    const bg = aviso.cor_bg && aviso.cor_bg.length > 0 ? aviso.cor_bg : 'transparent';
    const pillStyle = `--switch-aviso-color: ${aviso.cor_txt}; --switch-aviso-bg: ${bg}; anchor-name: ${anchorName};`;
    const texto = (aviso.texto || '').trim();
    const hasTexto = texto.length > 0;

    const buildPillInner = (host) => {
      if (aviso.icone_url) {
        const iconWrap = document.createElement('span');
        iconWrap.className = 'switch-aviso__icon';
        iconWrap.setAttribute('aria-hidden', 'true');
        const img = document.createElement('img');
        img.src = aviso.icone_url;
        img.alt = '';
        img.width = 12;
        img.height = 12;
        img.loading = 'lazy';
        iconWrap.appendChild(img);
        host.appendChild(iconWrap);
      }
      const label = document.createElement('span');
      label.className = 'switch-aviso__label';
      label.textContent = aviso.titulo;
      host.appendChild(label);
    };

    const pillClasses =
      'switch-aviso__pill button-unstyled' + (aviso.bordas ? ' switch-aviso__pill--bordered' : '');

    if (!hasTexto) {
      // Static pill: no popover, no aria-describedby, no cursor-help.
      const wrapper = document.createElement('span');
      wrapper.className = 'switch-aviso switch-aviso--static';
      wrapper.dataset.avisoId = aviso.id;

      const pill = document.createElement('span');
      pill.className = `${pillClasses} switch-aviso__pill--static`;
      pill.style.cssText = pillStyle;
      buildPillInner(pill);

      wrapper.appendChild(pill);
      return wrapper;
    }

    const wrapper = document.createElement('anchored-popover-component');
    wrapper.setAttribute('data-hover-triggered', 'true');
    wrapper.className = 'switch-aviso';
    wrapper.dataset.avisoId = aviso.id;

    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('ref', 'trigger');
    button.className = pillClasses;
    button.setAttribute('popovertarget', popoverId);
    button.setAttribute('aria-describedby', srId);
    button.style.cssText = pillStyle;
    buildPillInner(button);

    const srText = document.createElement('span');
    srText.id = srId;
    srText.className = 'switch-aviso__sr-text';
    srText.textContent = texto;

    const popover = document.createElement('div');
    popover.id = popoverId;
    popover.setAttribute('ref', 'popover');
    popover.className = 'switch-aviso__popover';
    popover.setAttribute('popover', 'auto');
    popover.setAttribute('role', 'tooltip');
    popover.style.cssText = `position-anchor: ${anchorName};`;
    popover.textContent = texto;

    wrapper.appendChild(button);
    wrapper.appendChild(srText);
    wrapper.appendChild(popover);

    return wrapper;
  }
}

if (!customElements.get('switch-avisos')) {
  customElements.define('switch-avisos', SwitchAvisos);
}
